import React, { useState, useEffect } from 'react';
import Card from './Card';

interface GachaCardAnimationProps {
  isOpen: boolean;
  onComplete: (result: { itemId?: string; itemName: string; imageUrl?: string; isBlank?: boolean }) => void;
  result: { itemId?: string; itemName: string; imageUrl?: string; isBlank?: boolean };
}

const GachaCardAnimation: React.FC<GachaCardAnimationProps> = ({
  isOpen,
  onComplete,
  result
}) => {
  const [isFlipping, setIsFlipping] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);
  const [showResult, setShowResult] = useState(false);

  // 카드 뒤집기 애니메이션
  const handleFlip = () => {
    if (isFlipping) return;
    setIsFlipping(true);

    setTimeout(() => {
      setIsFlipped(true);
      setShowResult(true);
      setIsFlipping(false);

      setTimeout(() => {
        onComplete(result);
      }, 1500);
    }, 300);
  };

  // 카드 셔플 애니메이션
  useEffect(() => {
    if (!isOpen) return;

    const shuffleInterval = setInterval(() => {
      setCardIndex(prev => (prev + 1) % 3);
    }, 200);

    return () => clearInterval(shuffleInterval);
  }, [isOpen]);

  // 초기화
  useEffect(() => {
    if (isOpen) {
      setIsFlipped(false);
      setIsFlipping(false);
      setCardIndex(0);
      setShowResult(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      fontFamily: 'Pretendard'
    }}>
      <Card
        width="400px"
        padding="32px"
        borderRadius="16px"
        background="white"
        boxShadow="0px 8px 16px rgba(0, 0, 0, 0.12)"
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          alignItems: 'center'
        }}>
          <h2 style={{
            margin: 0,
            color: 'var(--Color-text-normal, #161616)',
            fontSize: '24px',
            fontFamily: 'pretendard',
            fontWeight: '600'
          }}>
            카드 뽑기
          </h2>

          <div
            style={{
              width: '300px',
              height: '400px',
              position: 'relative',
              perspective: '1000px',
              cursor: isFlipped ? 'default' : 'pointer'
            }}
            onClick={!isFlipped ? handleFlip : undefined}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                position: 'relative',
                transformStyle: 'preserve-3d',
                transition: isFlipping ? 'transform 0.6s' : 'none',
                transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
              }}
            >
              {/* 카드 앞면 */}
              <div style={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                backfaceVisibility: 'hidden',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                border: '2px solid var(--Color-line-normal, #F0F0F0)',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  fontSize: '48px',
                  animation: cardIndex === 0 ? 'none' : 'none'
                }}>
                  {cardIndex === 0 ? '🃏' : cardIndex === 1 ? '🂡' : '🂮'}
                </div>
                <div style={{
                  color: 'white',
                  fontSize: '20px',
                  fontFamily: 'pretendard',
                  fontWeight: '600',
                  textAlign: 'center'
                }}>
                  {isFlipped ? '' : '카드를 클릭하세요'}
                </div>
              </div>

              {/* 카드 뒷면 (결과) */}
              <div style={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
                borderRadius: '12px',
                background: result.isBlank ? '#FFEBEE' : '#E8F5E9',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                border: '2px solid var(--Color-line-normal, #F0F0F0)',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
              }}>
                {result.isBlank ? (
                  <>
                    <span style={{ fontSize: '80px' }}>❌</span>
                    <div style={{
                      color: '#C62828',
                      fontSize: '32px',
                      fontFamily: 'pretendard',
                      fontWeight: '600'
                    }}>
                      꽝
                    </div>
                  </>
                ) : (
                  <>
                    {result.imageUrl ? (
                      <img
                        src={result.imageUrl}
                        alt={result.itemName}
                        style={{
                          width: '150px',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: '80px' }}>📦</span>
                    )}
                    <div style={{
                      color: '#2E7D32',
                      fontSize: '24px',
                      fontFamily: 'pretendard',
                      fontWeight: '600',
                      textAlign: 'center',
                      padding: '0 16px'
                    }}>
                      {result.itemName}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {!isFlipped && (
            <div style={{
              color: 'var(--Color-text-assistive, #989898)',
              fontSize: '14px',
              fontFamily: 'pretendard',
              fontWeight: '400',
              textAlign: 'center'
            }}>
              카드를 클릭하여 결과를 확인하세요
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default GachaCardAnimation;
